<template>
  <div class="filter-container" ref="filterContainer">
    <div class="k-filter-dropdown">
      <button
        class="k-filter-dropdown-button btn btn-outlined"
        :class="showFilters ? 'active' : ''"
        aria-label="Filters" title="Filters" @click="toggleFilterDropdown"
        ref="kdropdown">
        <i class="fas fa-filter" aria-hidden="true"></i> Filters
        <span class="active-filters-num" v-if="numberOfSelectedFilters > 0">- {{ numberOfSelectedFilters }} selected</span>
      </button>
    </div>
    <ul class="filters-list" v-if="showFilters">
      <li v-for="filter in filterOptions" :key="filter.key">
        <label class="k-custom-checkbox">
          <input type="checkbox" :value="filter.key" v-model="filterToggleState[filter.key]" class="hidden-checkbox"/>
          <span class="k-custom-checkbox-box ph-no-capture"></span>
          <span class="k-custom-checkbox-text">{{ filter.text }}</span>
        </label>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.filter-container .k-filter-dropdown-button.btn-outlined:focus,
.filter-container .k-filter-dropdown-button.btn-outlined.active {
  background: var(--kate-background-body-alpha);
  border-color: var(--kate-secondary);
  color: var(--kate-type-light);
}

.filter-container .k-filter-dropdown-button i {
  padding-right: 5px;
}

.active-filters-num {
  color: var(--kate-type-primary);
  font-weight: 400;
  font-size: 0.9em;
}

.btn-outlined:hover .active-filters-num {
  color: var(--kate-type-dark);
}

.filter-container {
  position: relative;
}

.filter-container .k-filter-dropdown-button:focus:hover {
  color: var(--kate-type-light);
}

.filters-list {
  position: absolute;
  right: 0;
  z-index: 1;
  min-width: 280px;
  padding: 15px;
  list-style: none;
  background-color: var(--input-background);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: var(--box-shadow);
}

.filters-list li .k-custom-checkbox-text {
  color: var(--kate-type-light);
  padding: 5px;
}

.filters-list li .k-custom-checkbox-text,
.filters-list .k-custom-checkbox-box {
  cursor: pointer;
}

.filters-list li:hover .k-custom-checkbox-text {
  color: var(--kate-secondary);
}

input.hidden-checkbox:checked + .k-custom-checkbox-box {
  background-color: var(--kate-secondary);
}

input.hidden-checkbox:checked + .k-custom-checkbox-box::after {
  content: "";
  display: block;
  position: absolute;
  top: 2px;
  left: 6px;
  width: 5px;
  height: 10px;
  border: solid var(--kate-background-body);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
</style>

<script>

export default {

  props: {
    defaultSettings: {
      type: Object,
      default: () => ({}),
    },
    filterOptions: {
      type: Array,
      require: true,
      validator(value) {
        if (value instanceof Array) {
          for (let i = 0; i < value.length; i++) {
            const hasFilterProperties = value[i].text && value[i].key;
            if (!hasFilterProperties) {
              return false;
            }
          }
          return true;
        }
        return false;
      },
    },
  },

  data() {
    return {
      showFilters: false,
      // Keeps track of true / false for each filter option
      filterToggleState: {},
    };
  },

  mounted() {
    this.loadFromRoute();
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    filterToggleState: {
      handler() {
        this.handleFilterUpdate();
      },
      deep: true,
    },
  },

  computed: {
    activeFilters() {
      return this.filterOptions.filter(x => this.filterToggleState[x.key]);
    },
    numberOfSelectedFilters() {
      return this.activeFilters.length;
    },
  },

  methods: {
    toggleFilterDropdown() {
      this.showFilters = !this.showFilters;
    },

    handleClickOutside(event) {
      if (!this.showFilters) {
        return;
      }
      const filterContainer = this.$refs.filterContainer;
      if (filterContainer && !filterContainer.contains(event.target)) {
        this.showFilters = false;
      }
    },

    handleFilterUpdate() {
      this.updateRouteQueryParams();
      this.$emit('filter', this.activeFilters);
    },

    updateRouteQueryParams() {
      // Vue doesn't like it if you update the route without changes
      // This method checks that the value actually changes before updating.
      // const filterKeys = Object.keys(this.filterToggleState);
      const filterKeys = this.filterOptions.map(x => x.key);
      const query = { ...this.$route.query };
      let key;
      let change = false;
      let queryVal;
      let jsonVal;

      for (let i = 0; i < filterKeys.length; i++) {
        key = filterKeys[i];
        queryVal = query[key];
        if (this.filterToggleState[key] || this.defaultSettings[key]) {
          // For filters that are set to true by default, they need to be explicitly set to false if when off,
          // otherwise they will be overridden by the default settings when sharing urls.
          jsonVal = JSON.stringify(this.filterToggleState[key]);
          if (jsonVal !== queryVal) {
            query[key] = jsonVal;
            change = true;
          }
        } else if (queryVal) {
          delete query[key];
          change = true;
        }
      }
      if (change) {
        this.$router.replace({ query });
      }
    },

    loadFromRoute() {
      const filterOptionsKeys = this.filterOptions.map(x => x.key);
      const query = this.$route.query;
      let filterToggleState = {};
      if (Object.keys(query).length) {
        filterOptionsKeys.forEach(key => {
          if (query[key] !== undefined) {
            filterToggleState[key] = JSON.parse(query[key]);
          }
        });
      } else {
        filterToggleState = { ...this.defaultSettings };
      }
      this.filterToggleState = filterToggleState;
    },
  },
};
</script>
